"use client"

import React, { useState, useMemo } from "react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/blades/blade-dialog"
import { Play } from "lucide-react"
import { toast } from "sonner"
import { useTestMappings } from "@/hooks/use-test-mappings"
import { TestResultsFull } from "@/components/testing/test-results-full"
import { MappingSelector } from "@/components/testing/mapping-selector"
import type { FullTestResults, FlatMapping } from "@/types/ui"
import type { MappingConfig } from "@/types/fbo-api"
import { getValidMappedFieldNames, countMappedFields } from "@/utils/mapping-helpers"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

interface FullTestModalProps {
  isOpen: boolean
  onClose: () => void
  allMappings: FlatMapping[]
  config: MappingConfig
  primaryEntityId: string
}

export function FullTestModal({ isOpen, onClose, allMappings, config, primaryEntityId }: FullTestModalProps) {
  const { t, n } = useTranslation()
  const [selectedMappings, setSelectedMappings] = useState<Set<string>>(new Set())
  const [testResults, setTestResults] = useState<FullTestResults | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [validationMessage, setValidationMessage] = useState<string | null>(null)
  const [hasInitializedSelections, setHasInitializedSelections] = useState(false)

  const { testFullMappings } = useTestMappings()

  // Initialize selected mappings when modal opens for the first time
  React.useEffect(() => {
    if (isOpen && !hasInitializedSelections) {
      // Select all valid mapped fields by default
      const allValidMappedFields = getValidMappedFieldNames(allMappings)
      setSelectedMappings(new Set(allValidMappedFields))
      setHasInitializedSelections(true)
    }
  }, [isOpen, allMappings, hasInitializedSelections])

  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setTestResults(null)
      setValidationMessage(null)
      setIsLoading(false)
      setHasInitializedSelections(false)
    }
  }, [isOpen])

  // Create filtered config with only selected mappings
  const filteredConfig = useMemo(() => {
    if (selectedMappings.size === 0) return config

    return {
      ...config,
      entities: config.entities.map(entity => ({
        ...entity,
        fieldMappings: entity.fieldMappings.filter(mapping => 
          selectedMappings.has(mapping.destinationField)
        )
      }))
    }
  }, [config, selectedMappings])

  const handleTest = async () => {
    if (selectedMappings.size === 0) {
      toast.error(t("testing.toasts.noSelectionTitle"), {
        description: t("testing.toasts.noSelection"),
      })
      return
    }

    setIsLoading(true)
    setTestResults(null)
    setValidationMessage(null)

    try {
      // Using the exact same logic from the original handleTestMappings function
      const result = await testFullMappings({
        primaryEntityId,
        config: filteredConfig,
      })

      if (result.success && result.data) {
        toast.success(t("testing.toasts.completeTitle"), {
          description: t((selectedMappings.size === 1 ? "testing.toasts.fullCompleteOne" : "testing.toasts.fullCompleteOther"), { count: n(selectedMappings.size) }),
        })
        setTestResults(result.data)
      } else {
        setValidationMessage(result.validationMessage || t("testing.errors.testFailed"))
      }
    } catch (error) {
      toast.error(t("testing.toasts.failedTitle"), {
        description: t("testing.toasts.failed"),
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleRunAnotherTest = () => {
    setTestResults(null)
    setValidationMessage(null)
  }

  const selectedCount = selectedMappings.size
  const totalMappedFields = countMappedFields(allMappings)

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{t("testing.fullMappings.title")}</DialogTitle>
          <DialogDescription>
            {t("testing.fullMappings.modalDescription", { id: primaryEntityId })}
          </DialogDescription>
        </DialogHeader>

         {!testResults && !validationMessage && (
           <div className="flex-1 overflow-y-auto">
             <div className="space-y-6 p-1">
               {/* Mapping Selection */}
               <MappingSelector
                 allMappings={allMappings}
                 selectedMappings={selectedMappings}
                 onSelectionChange={setSelectedMappings}
               />

               {/* Run Test Button */}
               <div className="flex justify-center pt-4 border-t">
                 <Button 
                   onClick={handleTest} 
                   disabled={isLoading || selectedCount === 0}
                   size="lg"
                 >
                   {isLoading ? (
                     <>
                       <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                       {t((selectedCount === 1 ? "testing.fullMappings.testingOne" : "testing.fullMappings.testingOther"), { count: n(selectedCount) })}
                     </>
                   ) : (
                     <>
                       <Play className="h-4 w-4 mr-2" />
                       {t((selectedCount === 1 ? "testing.fullMappings.runTestOne" : "testing.fullMappings.runTestOther"), { count: n(selectedCount) })}
                     </>
                   )}
                 </Button>
               </div>
             </div>
           </div>
         )}

         {/* Validation Error */}
         {validationMessage && (
           <div className="flex-1 flex items-center justify-center p-4">
             <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 max-w-md">
               <div className="flex items-start space-x-2">
                 <div className="flex-shrink-0">
                   <div className="w-5 h-5 rounded-full bg-destructive flex items-center justify-center">
                     <span className="text-white text-xs font-bold">!</span>
                   </div>
                 </div>
                 <div className="flex-1">
                   <h4 className="text-sm font-medium text-destructive">{t("testing.validation.title")}</h4>
                   <p className="text-sm text-destructive/80 mt-1">{validationMessage}</p>
                   <p className="text-xs text-destructive/60 mt-2">
                     {t("testing.validation.help")}
                   </p>
                 </div>
               </div>
             </div>
           </div>
         )}

         {/* Test Results - Give it full remaining height */}
         {testResults && (
           <div className="flex-1 min-h-0">
             <TestResultsFull results={testResults} allMappings={allMappings} />
           </div>
         )}

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            {t("common.actions.close")}
          </Button>
          {testResults && (
            <Button onClick={handleRunAnotherTest}>
              {t("testing.fullMappings.runAnother")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
